<script setup lang="ts">
import type { PropertyOnList } from '@@/shared/property/api-types';
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria';
import { IadPromoCardBlog, IadPromoCardConcierge, IadPromoCardSearchAdvisor, IadPromoCardValuation, IadPromoCardOverseas, IadPromoCardPropertips, IadPromoCardHomepilot } from '#components';

const props = defineProps<{
  properties: PropertyOnList[]
}>();

const { properties } = toRefs(props);

const propertiesGroups = computed(() => {
  //  Promotional cards have to be displayed at index 7 and 19
  const first = properties.value.slice(0, 6) ?? [];
  const second = properties.value.slice(6, 17) ?? [];
  const third = properties.value.slice(17, 30) ?? [];
  const groupedProperties = [first, second, third];
  return groupedProperties;
});

// Later IadPromoCardAlert & favorites
const ALL_PROMO_CARDS = [
  IadPromoCardValuation,
  IadPromoCardSearchAdvisor,
  IadPromoCardConcierge,
  IadPromoCardBlog,
  IadPromoCardPropertips,
  IadPromoCardOverseas,
  IadPromoCardHomepilot,
];

const RENTAL_PROMO_CARDS = [
  IadPromoCardSearchAdvisor,
  IadPromoCardConcierge,
  IadPromoCardBlog,
];

const route = useRoute();
const { propertySearchCriteria } = usePropertySearchCriteria(route);
const transactionType = toRef(() => propertySearchCriteria.value.transactionType);
const availablePromoCards = computed(() => transactionType?.value === 'rental' ? RENTAL_PROMO_CARDS : ALL_PROMO_CARDS);
const remainingAvailablePromoCardIndexes = ref([...availablePromoCards.value.keys()]);
function drawPromoCardIndex() {
  const randomAvailablePromoCardIndex = Math.floor(Math.random() * remainingAvailablePromoCardIndexes.value.length);
  const randomCardIndex = remainingAvailablePromoCardIndexes.value[randomAvailablePromoCardIndex] as number;
  // we remove the drawn card from the available cards
  remainingAvailablePromoCardIndexes.value = remainingAvailablePromoCardIndexes.value.toSpliced(randomAvailablePromoCardIndex, 1);
  return randomCardIndex;
};

const drawnPromoCardIndexes = useState(`drawn-promo-card-indexes-${transactionType.value}`, () => {
  remainingAvailablePromoCardIndexes.value = [...availablePromoCards.value.keys()];
  return [drawPromoCardIndex(), drawPromoCardIndex()];
});

watch(() => route.fullPath, resetPromoCards);
function resetPromoCards() {
  remainingAvailablePromoCardIndexes.value = [...availablePromoCards.value.keys()];
  drawnPromoCardIndexes.value = [drawPromoCardIndex(), drawPromoCardIndex()];
}

defineEmits<{
  alert: [PropertySearchCriteria]
}>();
</script>

<template>
  <div
    class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 py-m gap-xl max-w-screen-xl mx-auto"
  >
    <template v-for="(propertiesInGroup, groupIndex) of propertiesGroups">
      <PropertyCard
        v-for="property of propertiesInGroup"
        :key="property.propertyListingRef"
        :property
      />
      <component
        :is="availablePromoCards[drawnPromoCardIndexes[groupIndex]!]"
        v-if="propertiesGroups[groupIndex + 1]?.length"
        :key="groupIndex"
      />
    </template>
    <IadPromoCardAlert
      v-if="properties.length > 0"
      @alert="$emit('alert', propertySearchCriteria)"
    />
  </div>
</template>
